import { type JSX, type ReactNode, useCallback, useEffect, useState } from 'react';

import {
  BEREICHE,
  BEREICHSTEXTE,
  aendern as sendeAenderung,
  einrichten as sendeEinrichtung,
  schluesselNeu as sendeSchluesselNeu,
  zugaenge as holeZugaenge,
  type Bereich,
  type Portalzugang,
} from '../api/portalzugaenge';
import { ApiFehler } from '../api/fehler';
import { markenklasse } from '../marken';
import { Feldraster, Feldzeile } from '../komponenten/Akte';
import { Dialog } from '../komponenten/Dialog';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { useKopfhandlungen, useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/** Ortszeit, wie LOG-03 es für die Anzeige verlangt. */
function zeitpunkt(wert: string): string {
  return new Date(wert).toLocaleString('de-DE');
}

function tag(wert: string): string {
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? wert : d.toLocaleDateString('de-DE');
}

/**
 * Die Zugänge des Buchungsportals (BUC-29, BUC-30, BUC-31, BUC-16).
 *
 * Das Buchungsportal ist ein **eigenes Projekt**; dieses System liefert ihm
 * die Schnittstelle nach Kapitel 11 und legt ihren Umfang fest. Hier steht,
 * **wer sie benutzen darf** — mit welchem Umfang, in welcher Umgebung und mit
 * welchen Grenzwerten.
 *
 * ============================================================================
 * DER SCHLÜSSEL ERSCHEINT GENAU EINMAL
 * ============================================================================
 *
 * Er entsteht beim Einrichten, wird einmal angezeigt und ist danach fort — die
 * Datenbank trägt nur seinen Streuwert. Wer ihn verliert, setzt einen neuen;
 * der alte gilt dann **sofort** nicht mehr.
 *
 * Das ist unbequem und der Punkt: Ein Schlüssel, den man nachlesen kann, steht
 * in jeder Sicherung und in jedem Bildschirmfoto.
 *
 * ============================================================================
 * PRODUKTIV UND TEST SIND ZWEI ZUGÄNGE, NICHT ZWEI SERVER
 * ============================================================================
 *
 * Ein Testzugang darf Fehlerfälle **anfordern** („gib mir jetzt eine
 * ausgebuchte Antwort"), ein produktiver nie. Das Merkmal hängt am Zugang und
 * nicht an der Umgebungsvariablen des Servers — sonst wäre die Simulation auf
 * dem Produktivserver eine falsch gesetzte Variable entfernt.
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41)
 * ============================================================================
 *
 * Zähler und „Zugang einrichten …" im Seitenkopf, die Tabelle in der Karte,
 * die Pflege des gewählten Zugangs in der Seitenleiste. Die Seitenleiste
 * liest den Zugang aus der geladenen Liste nach seiner Kennung: Nach jeder
 * Änderung zeigt sie den Stand, den der Server zurückgab, und nicht den vom
 * Öffnen.
 */
export function Portalzugaenge({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  const [liste, setzeListe] = useState<readonly Portalzugang[] | null>(null);
  const [anlageOffen, setzeAnlageOffen] = useState(false);
  const [offenId, setzeOffenId] = useState<string | undefined>(undefined);
  const [geheimnis, setzeGeheimnis] = useState<
    { readonly kennung: string; readonly schluessel: string; readonly signatur: string | null } | undefined
  >(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [nachfrage, setzeNachfrage] = useState<'schliessen' | 'schluessel' | null>(null);

  const [kennung, setzeKennung] = useState('');
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [umgebung, setzeUmgebung] = useState<'produktiv' | 'test'>('test');
  const [umfang, setzeUmfang] = useState<readonly Bereich[]>([...BEREICHE]);
  const [ereignisAdresse, setzeEreignisAdresse] = useState('');

  const laden = useCallback(async (): Promise<void> => {
    setzeListe(await holeZugaenge());
  }, []);

  useEffect(() => {
    void laden().catch((ursache: unknown) => {
      setzeListe((alt) => alt ?? []);
      setzeFehler(ursache as ApiFehler);
    });
  }, [laden]);

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(liste === null ? undefined : zaehlwort(liste.length, 'Zugang', 'Zugänge'));

  /* BUC-29: Einrichten darf nur, wer Umsysteme verwaltet — die Haupthandlung im Seitenkopf. */
  useKopfhandlungen(
    darfPflegen ? (
      <button type="button" className="knopf knopf--haupt" onClick={() => setzeAnlageOffen(true)}>
        Zugang einrichten …
      </button>
    ) : undefined,
    [darfPflegen],
  );

  const zugaenge = liste ?? [];
  const offen = zugaenge.find((z) => z.id === offenId);

  /*
   * Die Rückfrage (CRM-104) läuft an `handle` vorbei: Ihr Fehler gehört ins
   * Pop-up, nicht über die Liste. Erst der Erfolg schließt sie.
   */
  async function bestaetigt(was: () => Promise<void>): Promise<void> {
    await was();
    setzeNachfrage(null);
    setzeFehler(undefined);
  }

  async function handle(was: () => Promise<void>): Promise<void> {
    setzeLaeuft(true);
    try {
      await was();
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  function umfangUmschalten(bereich: Bereich): void {
    setzeUmfang((bisher) =>
      bisher.includes(bereich) ? bisher.filter((b) => b !== bereich) : [...bisher, bereich],
    );
  }

  /*
   * DIE PFLEGE IN DER SEITENLEISTE. Die Felder sichern beim Verlassen, jedes
   * für sich (BUC-30) — dieselbe Art wie vorher im Fenster. Der Schlüssel am
   * Kasten gilt dem Zugang: Wechselt die Wahl, beginnen die Felder mit dessen
   * Werten und nicht mit denen des vorigen.
   */
  const pflege: ReactNode =
    offen === undefined ? (
      <>
        <h2>Zugang bearbeiten</h2>
        <p className="hilfe">
          „Bearbeiten …" an einer Zeile öffnet den Zugang hier: Grenzwerte ändern, den Zugang
          schließen oder wieder öffnen, einen neuen Schlüssel setzen.
        </p>
      </>
    ) : (
      <div key={offen.id}>
        <h2>{offen.bezeichnung}</h2>
        <Feldraster>
          <Feldzeile beschriftung="Kennung" wert={offen.kennung} />
          <Feldzeile beschriftung="Eingerichtet am" wert={zeitpunkt(offen.angelegtAm)} />
          <Feldzeile
            beschriftung="Ereignismeldungen an"
            wert={offen.ereignisAdresse}
            anzeige={offen.ereignisAdresse ?? 'keine Adresse — kein Rückweg'}
          />
          <Feldzeile
            beschriftung="Gültig bis"
            wert={offen.gueltigBis}
            anzeige={
              offen.gueltigBis === null ? (
                'unbefristet'
              ) : (
                <span className={markenklasse('aufmerksamkeit')}>{tag(offen.gueltigBis)}</span>
              )
            }
          />
        </Feldraster>

        <div className="feld">
          <label className="feld__beschriftung" htmlFor="pz-lesend">
            Lesende Aufrufe je Minute
          </label>
          <input
            id="pz-lesend"
            type="number"
            min={1}
            defaultValue={offen.ratenLesend}
            onBlur={(e) =>
              void handle(async () => {
                setzeListe(
                  await sendeAenderung(offen.id, { ratenLesend: Number(e.target.value) }),
                );
              })
            }
          />
        </div>

        <div className="feld">
          <label className="feld__beschriftung" htmlFor="pz-schreibend">
            Schreibende Aufrufe je Minute und Kunde
          </label>
          <input
            id="pz-schreibend"
            type="number"
            min={1}
            defaultValue={offen.ratenSchreibend}
            onBlur={(e) =>
              void handle(async () => {
                setzeListe(
                  await sendeAenderung(offen.id, { ratenSchreibend: Number(e.target.value) }),
                );
              })
            }
          />
        </div>

        <div className="feld">
          <label className="feld__beschriftung" htmlFor="pz-reservierungen">
            Gleichzeitig offene Reservierungen je Kunde
          </label>
          <input
            id="pz-reservierungen"
            type="number"
            min={1}
            defaultValue={offen.offeneReservierungen}
            onBlur={(e) =>
              void handle(async () => {
                setzeListe(
                  await sendeAenderung(offen.id, {
                    offeneReservierungen: Number(e.target.value),
                  }),
                );
              })
            }
          />
          <p className="feld__hilfe">
            Schützt die Kapazität: So viele Reservierungen kann ein Kunde des Portals höchstens
            gleichzeitig halten, ohne zu buchen. Kunde ist die Adresse, die das Portal meldet.
            Abgelaufene zählen nicht mit.
          </p>
        </div>

        <div className="feld">
          <label className="feld__beschriftung" htmlFor="pz-schreibend-gesamt">
            Schreibende Aufrufe je Minute, alle Kunden zusammen
          </label>
          <input
            id="pz-schreibend-gesamt"
            type="number"
            min={1}
            defaultValue={offen.ratenSchreibendGesamt}
            onBlur={(e) =>
              void handle(async () => {
                setzeListe(
                  await sendeAenderung(offen.id, {
                    ratenSchreibendGesamt: Number(e.target.value),
                  }),
                );
              })
            }
          />
        </div>

        <div className="feld">
          <label className="feld__beschriftung" htmlFor="pz-reservierungen-gesamt">
            Gleichzeitig offene Reservierungen, alle Kunden zusammen
          </label>
          <input
            id="pz-reservierungen-gesamt"
            type="number"
            min={1}
            defaultValue={offen.offeneReservierungenGesamt}
            onBlur={(e) =>
              void handle(async () => {
                setzeListe(
                  await sendeAenderung(offen.id, {
                    offeneReservierungenGesamt: Number(e.target.value),
                  }),
                );
              })
            }
          />
          <p className="feld__hilfe">
            Der Deckel für das ganze Portal (D02). Er darf nicht unter der Grenze je Kunde
            liegen.
          </p>
        </div>

        <div className="knopfreihe">
          <button
            type="button"
            className="knopf knopf--klein"
            disabled={laeuft}
            onClick={() => {
              if (offen.aktiv) {
                setzeNachfrage('schliessen');
                return;
              }
              // Wieder öffnen stellt den vorigen Stand her und fragt nicht nach (CRM-104).
              void handle(async () => {
                setzeListe(await sendeAenderung(offen.id, { aktiv: true }));
              });
            }}
          >
            {offen.aktiv ? 'Zugang schließen …' : 'Zugang wieder öffnen'}
          </button>

          <button
            type="button"
            className="knopf knopf--klein knopf--eingriff"
            disabled={laeuft}
            onClick={() => setzeNachfrage('schluessel')}
          >
            Neuen Schlüssel setzen …
          </button>
        </div>

        <p className="feld__hilfe">
          Ein neuer Schlüssel macht den alten <strong>sofort</strong> ungültig. Wer ohne Ausfall
          wechseln will, richtet einen zweiten Zugang ein, stellt das Portal um und schließt den
          ersten.
        </p>

        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={() => setzeOffenId(undefined)}>
            Fertig
          </button>
        </div>
      </div>
    );

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      {/*
        DAS GEHEIMNIS STEHT ÜBER DER LISTE UND BLEIBT STEHEN, BIS JEMAND ES
        WEGKLICKT. Es verschwindet nicht von selbst — wer es übersieht, hätte
        sonst einen Zugang ohne Schlüssel eingerichtet.
      */}
      {geheimnis !== undefined && (
        <div className="hinweiskarte" role="status">
          <p>
            <strong>Der Schlüssel für „{geheimnis.kennung}" steht genau hier und nirgends
            sonst.</strong>
          </p>
          <p>
            Zugangsschlüssel: <code>{geheimnis.schluessel}</code>
          </p>
          {geheimnis.signatur !== null && (
            <p>
              Signaturgeheimnis der Ereignismeldungen: <code>{geheimnis.signatur}</code>
            </p>
          )}
          <p className="feld__hilfe">
            Geben Sie beides an das Portalteam weiter und schließen Sie diesen Hinweis. Danach
            lässt sich der Schlüssel nicht mehr anzeigen — die Datenbank trägt nur seinen
            Streuwert. Wer ihn verliert, setzt einen neuen; der alte gilt dann sofort nicht mehr.
          </p>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf knopf--klein"
              onClick={() => setzeGeheimnis(undefined)}
            >
              Habe ich notiert
            </button>
          </div>
        </div>
      )}

      <Liste
        hinweise={
          <p className="hilfe">
            Die Grenzwerte stammen aus dem Lastenheft und sind hier änderbar, ohne dass etwas neu
            ausgeliefert werden muss. Die Zahl der gleichzeitig offenen Reservierungen schützt die
            Kapazität und nicht den Server: Wer sie erhöht, gibt einem einzelnen Absender mehr
            Plätze zum Blockieren.
          </p>
        }
        vorschauBeschriftung="Zugang bearbeiten"
        vorschau={darfPflegen ? pflege : undefined}
      >
        {liste === null && <p role="status">Wird geladen …</p>}
        {liste !== null && zugaenge.length === 0 && (
          <div className="liste__leer" role="status">
            <p>Noch kein Zugang zur Buchungsschnittstelle eingerichtet.</p>
            {darfPflegen && (
              <button
                type="button"
                className="knopf knopf--haupt"
                onClick={() => setzeAnlageOffen(true)}
              >
                Zugang einrichten …
              </button>
            )}
          </div>
        )}

        {zugaenge.length > 0 && (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption className="nur-vorlesen">{zugaenge.length} Zugänge</caption>
              <thead>
                <tr>
                  <th scope="col">Kennung</th>
                  <th scope="col">Umgebung</th>
                  <th scope="col">Umfang</th>
                  <th scope="col">Grenzwerte je Minute</th>
                  <th scope="col">Zustand</th>
                  {darfPflegen && <th scope="col">Handlung</th>}
                </tr>
              </thead>
              <tbody>
                {zugaenge.map((z) => (
                  <tr
                    key={z.id}
                    className={
                      z.id === offenId
                        ? 'liste__zeile--gewaehlt'
                        : z.aktiv
                          ? undefined
                          : 'liste__zeile--blass'
                    }
                  >
                    <td>
                      <span className="liste__name">{z.bezeichnung}</span>
                      <span className="kanalliste__name"> · {z.kennung}</span>
                      {z.ereignisAdresse !== null && (
                        <span className="kanalliste__name"> · meldet an {z.ereignisAdresse}</span>
                      )}
                    </td>
                    <td>
                      {/* BUC-16: Nur ein Testzugang darf Fehlerfälle anfordern. */}
                      {z.umgebung === 'test' ? (
                        <span className={markenklasse('stillgelegt')}>Testumgebung</span>
                      ) : (
                        <span className={markenklasse('laufend')}>produktiv</span>
                      )}
                    </td>
                    <td>{z.umfang.join(', ')}</td>
                    <td>
                      {z.ratenLesend} lesend · {z.ratenSchreibend} schreibend je Kunde (
                      {z.ratenSchreibendGesamt} gesamt) · {z.offeneReservierungen} offene Reservierungen
                      je Kunde ({z.offeneReservierungenGesamt} gesamt)
                    </td>
                    <td>
                      {z.aktiv ? (
                        <span className={markenklasse('laufend')}>aktiv</span>
                      ) : (
                        <span className={markenklasse('sackgasse')}>geschlossen</span>
                      )}
                      {/* Eine Frist in Rost: Danach weist die Schnittstelle den Zugang ab. */}
                      {z.aktiv && z.gueltigBis !== null && (
                        <>
                          {' '}
                          <span className={markenklasse('aufmerksamkeit')}>
                            gültig bis {tag(z.gueltigBis)}
                          </span>
                        </>
                      )}
                      {z.letzterAufruf === null ? (
                        <span className="kanalliste__name"> · nie gerufen</span>
                      ) : (
                        <span className="kanalliste__name"> · zuletzt {zeitpunkt(z.letzterAufruf)}</span>
                      )}
                    </td>
                    {darfPflegen && (
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          aria-pressed={z.id === offenId}
                          onClick={() => setzeOffenId(z.id)}
                        >
                          Bearbeiten …
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>

      {/* BUC-29: Ein geschlossener Zugang weist jeden Aufruf des Portals ab. */}
      {offen !== undefined && nachfrage === 'schliessen' && (
        <Rueckfrage
          frage="Zugang schließen?"
          unterzeile={`${offen.bezeichnung} · ${offen.kennung}`}
          tat="Schließen"
          beiSchliessen={() => setzeNachfrage(null)}
          beiBestaetigen={() =>
            bestaetigt(async () => {
              setzeListe(await sendeAenderung(offen.id, { aktiv: false }));
            })
          }
        >
          <p>
            Die Schnittstelle weist ab sofort jeden Aufruf von „{offen.kennung}" ab (
            {offen.letzterAufruf === null
              ? 'bisher nie gerufen'
              : `zuletzt gerufen am ${zeitpunkt(offen.letzterAufruf)}`}
            ). Der Schlüssel bleibt gültig; „Zugang wieder öffnen" lässt das Portal wieder zu.
          </p>
        </Rueckfrage>
      )}

      {/* BUC-29: Ein neuer Schlüssel macht den alten sofort ungültig. */}
      {offen !== undefined && nachfrage === 'schluessel' && (
        <Rueckfrage
          frage="Neuen Schlüssel setzen?"
          unterzeile={`${offen.bezeichnung} · ${offen.kennung}`}
          tat="Neuen Schlüssel setzen"
          beiSchliessen={() => setzeNachfrage(null)}
          beiBestaetigen={() =>
            bestaetigt(async () => {
              const neu = await sendeSchluesselNeu(offen.id);
              setzeGeheimnis({ kennung: offen.kennung, schluessel: neu.schluessel, signatur: null });
              void laden().catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
            })
          }
        >
          <p>
            Der bisherige Schlüssel von „{offen.kennung}" gilt sofort nicht mehr (
            {offen.letzterAufruf === null
              ? 'bisher nie gerufen'
              : `zuletzt gerufen am ${zeitpunkt(offen.letzterAufruf)}`}
            ): Das Portal wird abgewiesen, bis der neue dort eingetragen ist. Der neue Schlüssel
            erscheint genau einmal.
          </p>
        </Rueckfrage>
      )}

      {anlageOffen && (
        <Dialog titel="Zugang einrichten" beiSchliessen={() => setzeAnlageOffen(false)}>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pz-kennung">
              Kennung
            </label>
            <input
              id="pz-kennung"
              value={kennung}
              onChange={(e) => setzeKennung(e.target.value)}
              placeholder="portal-test"
            />
            <p className="feld__hilfe">
              Drei bis vierzig Zeichen, beginnend mit einem Kleinbuchstaben; erlaubt sind
              Kleinbuchstaben, Ziffern und Bindestriche. Sie ist der Benutzername des Zugangs und
              steht in jedem Protokolleintrag.
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pz-bezeichnung">
              Bezeichnung
            </label>
            <input
              id="pz-bezeichnung"
              value={bezeichnung}
              onChange={(e) => setzeBezeichnung(e.target.value)}
              placeholder="Buchungsportal, Testumgebung"
            />
          </div>

          <fieldset className="feld">
            <legend className="feld__beschriftung">Umgebung</legend>
            <div className="wahlreihe">
              <label>
                <input
                  type="radio"
                  name="pz-umgebung"
                  checked={umgebung === 'test'}
                  onChange={() => setzeUmgebung('test')}
                />{' '}
                Testumgebung
              </label>
              <label>
                <input
                  type="radio"
                  name="pz-umgebung"
                  checked={umgebung === 'produktiv'}
                  onChange={() => setzeUmgebung('produktiv')}
                />{' '}
                produktiv
              </label>
            </div>
            <p className="feld__hilfe">
              Ein Testzugang darf Fehlerfälle anfordern und bucht nicht wirklich. Ein produktiver
              bucht — jede Anfrage erzeugt einen echten Vertrag.
            </p>
          </fieldset>

          <fieldset className="feld">
            <legend className="feld__beschriftung">Umfang</legend>
            <div className="wahlliste">
              {BEREICHE.map((b) => (
                <label key={b}>
                  <input
                    type="checkbox"
                    checked={umfang.includes(b)}
                    onChange={() => umfangUmschalten(b)}
                  />{' '}
                  {b} — {BEREICHSTEXTE[b]}
                </label>
              ))}
            </div>
            <p className="feld__hilfe">
              Was hier nicht steht, ist für diesen Zugang geschlossen. Ein Zugang, der nur den
              Katalog zeigt, kann nicht buchen — auch dann nicht, wenn seine Zugangsdaten stimmen.
            </p>
          </fieldset>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pz-ereignis">
              Adresse für Ereignismeldungen (freiwillig)
            </label>
            <input
              id="pz-ereignis"
              value={ereignisAdresse}
              onChange={(e) => setzeEreignisAdresse(e.target.value)}
              placeholder="https://portal.example/crm-ereignisse"
            />
            <p className="feld__hilfe">
              Wohin dieses System Terminänderung, Terminabsage, Buchungsbestätigung und Stornierung
              meldet — signiert und mit Wiederholung bei
              Fehlschlag. Nur über https; das Signaturgeheimnis entsteht dabei und wird einmal
              angezeigt. Ohne Adresse gibt es keinen Rückweg.
            </p>
          </div>

          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => setzeAnlageOffen(false)}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laeuft || kennung.trim() === '' || umfang.length === 0}
              onClick={() =>
                void handle(async () => {
                  const neu = await sendeEinrichtung({
                    kennung: kennung.trim(),
                    bezeichnung: bezeichnung.trim() === '' ? kennung.trim() : bezeichnung.trim(),
                    umgebung,
                    umfang,
                    ereignisAdresse:
                      ereignisAdresse.trim() === '' ? null : ereignisAdresse.trim(),
                  });
                  setzeGeheimnis({
                    kennung: kennung.trim(),
                    schluessel: neu.schluessel,
                    signatur: neu.signaturgeheimnis,
                  });
                  setzeAnlageOffen(false);
                  setzeKennung('');
                  setzeBezeichnung('');
                  setzeEreignisAdresse('');
                  await laden();
                })
              }
            >
              Einrichten
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
